import omit from 'lodash/omit';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { deserializeValues, getFilterKeys, serializeValues } from './serialize';
import { FilterField, FilterValues } from './types';

interface UseUrlFiltersOptions {
  /**
   * Дополнительные ключи URL, которые нужно сбросить вместе с фильтрами
   * (например, `page`, чтобы при смене фильтров вернуться на первую страницу).
   * По умолчанию: ['page'].
   */
  resetKeys?: string[];
}

interface UseUrlFiltersResult {
  /** Текущие значения фильтров, прочитанные из URL. */
  values: FilterValues;
  /** Дефолты формы — те же что и values, но с пустыми ключами для всех полей. */
  defaultValues: FilterValues;
  /** Сериализованный для API/queryKey плоский объект. */
  searchParams: Record<string, string>;
  /** Записать значения формы в URL (с сохранением остальных параметров). */
  applyFilters: (values: FilterValues) => void;
  /** Очистить только ключи фильтра в URL. */
  resetFilters: () => void;
}

/**
 * Связывает набор полей фильтра с URL: чтение текущих значений,
 * запись/очистка с сохранением посторонних параметров (search, sort и т.п.).
 */
export const useUrlFilters = (
  fields: FilterField[],
  options: UseUrlFiltersOptions = {},
): UseUrlFiltersResult => {
  const history = useHistory();
  const location = useLocation();

  const { resetKeys = ['page'] } = options;

  const params = useMemo(
    () => (location.search ? qs.parse(location.search.slice(1)) : {}) as Record<string, unknown>,
    [location.search],
  );

  const filterKeys = useMemo(() => getFilterKeys(fields), [fields]);

  const values = useMemo(() => deserializeValues(params, fields), [params, fields]);

  const defaultValues = useMemo<FilterValues>(() => {
    // Используем `null` (а не `undefined`) для пустых значений: это заставляет
    // react-hook-form v6 при reset()/register корректно вызвать setFieldValue,
    // очищая внутренний valueRef Controller-а. С `undefined` RHF пропустил бы
    // применение значения и старые данные всплывали бы при следующем submit.
    const empty: FilterValues = {};
    for (const f of fields) {
      empty[f.name] = null;
      if (f.nameTo) empty[f.nameTo] = null;
    }
    return { ...empty, ...values };
  }, [fields, values]);

  const searchParams = useMemo(() => serializeValues(values, fields), [values, fields]);

  const applyFilters = useCallback(
    (next: FilterValues) => {
      const filterParams = serializeValues(next, fields);
      const rest = omit(params, [...filterKeys, ...resetKeys]);
      const search = qs.stringify({ ...rest, ...filterParams });
      history.push({ search });
    },
    [history, params, fields, filterKeys, resetKeys],
  );

  const resetFilters = useCallback(() => {
    const rest = omit(params, [...filterKeys, ...resetKeys]);
    const search = qs.stringify(rest);
    history.push({ search });
  }, [history, params, filterKeys, resetKeys]);

  return {
    values,
    defaultValues,
    searchParams,
    applyFilters,
    resetFilters,
  };
};
